import React from "react";

import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'

import ViewModuleIcon from '@mui/icons-material/ViewModule';

import { BLOCKSTREAM_EXPLORER_URL } from '../../../../config';

import BCTable from '../Utils/BCTable';
import moment from "moment";
import useAmpAssets from "../../../../api/blockchain/useAmpAssets";
import useHolderTransactions from "../../../../api/blockchain/useHolderTransactions";
import DataLoading from "../Utils/DataLoading";
import NoData from "../Utils/NoData";
import DataError from "../Utils/DataError";

const HolderTransactionsDialog = ({ open, handleClose, user }) => {
    const [value, setValue] = React.useState(null);
    const [inputValue, setInputValue] = React.useState('');

    const { ampAssets, ampAssetsLoading } = useAmpAssets()
    const { holderTransactions, holderTransactionsLoading, holderTransactionsError } = useHolderTransactions(user?.id, value?.assetUuid)

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='lg'>
            <DialogTitle>Holder transactions</DialogTitle>
            <DialogContent>
                <Stack direction="row" alignItems="center" justifyContent='space-between' gap={12}>
                    <div>
                        <Typography variant='body1' as='h5'>
                            {user?.holder}
                        </Typography>
                        <Typography variant='caption' as='p' sx={{ color: 'gray' }}>
                            Holder since {moment(user?.created_at).format("MMM DD, YYYY")}
                            {
                                user?.registered_at &&
                                <><br />Registered since {moment(user?.registered_at).format("MMM DD, YYYY")}</>
                            }
                        </Typography>
                    </div>
                </Stack>
                <br />
                <Typography variant='body1' as='h5'>
                    Transactions
                </Typography>
                <div style={{ display: 'flex', color: 'gray' }}>
                    <Typography variant='body2'>
                        View and filter all holder transactions
                    </Typography>
                </div>
                <br />
                <Autocomplete
                    id="combo-box-demo"
                    options={ampAssets}
                    getOptionLabel={(option) => `${option['name']} (${option['ticker']})`}
                    fullWidth
                    loading={ampAssetsLoading}
                    value={value}
                    onChange={(event, newValue) => {
                        setValue(newValue);
                    }}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    sx={{ marginTop: '10px;', marginBottom: '20px', float: 'right' }}
                    renderInput={(params) => <TextField {...params} label="Assets" placeholder="Select an asset to see transactions" />}
                />
                {
                    value && <>
                        <DataLoading
                            loading={holderTransactionsLoading}
                            text='Loading holder transactions for the asset...'
                        />
                        {
                            (!holderTransactionsLoading && holderTransactions) &&
                            <BCTable
                                sx={{ marginTop: '10px' }}
                                columns={[
                                    { name: 'description', label: 'Description', bold: true, extraInfoColumn: 'extra' },
                                    { name: 'parsedAmount', label: 'Amount', type: 'amount' },
                                    { name: 'from', label: 'From' },
                                    { name: 'to', label: 'To' },
                                    { name: 'datetime', label: 'Date', type: 'date', format: 'MMM DD, YYYY' },
                                    {
                                        name: 'actions', label: 'Actions', type: 'actions', render: (row) => (
                                            <IconButton variant="outlined" size="small"
                                                sx={{ marginRight: '10px;' }}
                                                onClick={() => window.open(
                                                    `${BLOCKSTREAM_EXPLORER_URL}/tx/${row.txid}#blinded=${row.amount},${value?.assetId},${row.amountBlinder},${row.assetBlinder}`,
                                                    '_blank'
                                                )}
                                            >
                                                <Tooltip title="View on-chain">
                                                    <ViewModuleIcon />
                                                </Tooltip>
                                            </IconButton>
                                        )
                                    }
                                ]}
                                data={holderTransactions}
                            />
                        }
                        <br />
                        {/* No data */}
                        <NoData
                            show={!holderTransactionsLoading && !holderTransactionsError && holderTransactions.length === 0}
                            text='There are no transactions of this asset.'
                        />

                        {/* AMP asset error */}
                        <DataError
                            show={holderTransactionsError && (!holderTransactions || holderTransactions.length === 0)}
                            text='Could not load holder transactions due to error. Please try again.'
                        />
                    </>
                }
                {
                    !value &&
                    <Typography>
                        Select an asset to see the transactions.
                    </Typography>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog >
    )
}

export default HolderTransactionsDialog